import { useState } from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { decodePack, resolvePackItems, type PackItem } from "@/lib/pack";
import { copyText } from "@/lib/copy";

interface PackLoaderData {
  title: string;
  items: PackItem[];
}

export const Route = createFileRoute("/pack/$data")({
  loader: ({ params }): PackLoaderData => {
    const pack = decodePack(params.data);
    if (!pack) throw notFound();
    const items = resolvePackItems(pack.symbols);
    if (items.length === 0) throw notFound();
    return { title: pack.title, items };
  },
  head: ({ loaderData }: { loaderData?: PackLoaderData }) => {
    const title = loaderData?.title?.trim();
    const heading = title && title.length > 0 ? title : "Shared symbol pack";
    const count = loaderData?.items.length ?? 0;
    return {
      // User-generated content — keep packs out of the index (Phase 17 spec).
      meta: [
        { title: heading },
        { name: "robots", content: "noindex, follow" },
        {
          name: "description",
          content: `A shared pack of ${count} symbols on CoolSymbols.cool. Copy any symbol or the whole set.`,
        },
        { property: "og:title", content: heading },
      ],
    };
  },
  component: PackView,
});

function PackView() {
  const { title, items } = Route.useLoaderData();
  const [copiedAll, setCopiedAll] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const heading = title && title.trim().length > 0 ? title.trim() : "Shared symbol pack";

  async function copyOne(item: PackItem) {
    const ok = await copyText(item.symbol, { source: "pack-item", symbol: item.symbol });
    if (!ok) return;
    setCopied(item.symbol);
    window.setTimeout(() => setCopied((c) => (c === item.symbol ? null : c)), 1200);
  }

  async function copyAll() {
    const ok = await copyText(items.map((i: PackItem) => i.symbol).join(""), {
      source: "pack-all",
    });
    if (!ok) return;
    setCopiedAll(true);
    window.setTimeout(() => setCopiedAll(false), 1500);
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
          <Link to="/pack" className="hover:text-foreground">
            Symbol packs
          </Link>
          <span aria-hidden="true"> / </span>
          <span className="text-foreground/80">Shared pack</span>
        </nav>

        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-4xl font-bold sm:text-5xl">{heading}</h1>
          <button
            type="button"
            onClick={copyAll}
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            {copiedAll ? "Copied all!" : `Copy all ${items.length}`}
          </button>
        </div>
        <p className="mt-3 text-muted-foreground">
          {items.length} symbol{items.length === 1 ? "" : "s"} — tap any one to copy it, or open its
          page for every format.
        </p>

        <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {items.map((item: PackItem) => (
            <li key={item.symbol} className="glass flex flex-col items-center gap-2 rounded-xl p-4">
              <button
                type="button"
                onClick={() => copyOne(item)}
                aria-label={`Copy ${item.name}`}
                className="text-4xl leading-none transition-transform hover:scale-110"
              >
                {item.symbol}
              </button>
              <span className="line-clamp-2 text-center text-xs text-muted-foreground">
                {copied === item.symbol ? "Copied!" : item.name}
              </span>
              <Link
                to="/symbol/$slug"
                params={{ slug: item.slug }}
                className="text-[10px] uppercase tracking-wider text-primary hover:underline"
              >
                Details
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 border-t border-border pt-6">
          <Link
            to="/pack"
            className="inline-flex rounded-md border border-border bg-surface/60 px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            Build your own pack →
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
